<template>
  <div class="word-count">
    <h1>字数统计工具</h1>
    
    <div class="count-container">
      <!-- 输入区域 -->
      <div class="section">
        <div class="section-header">
          <h3>输入文本</h3>
          <div class="actions">
            <el-button size="small" @click="pasteFromClipboard">从剪贴板粘贴</el-button>
            <el-button size="small" @click="clearText">清空</el-button>
          </div>
        </div>
        <el-input
          v-model="text"
          type="textarea"
          :rows="12"
          placeholder="请输入或粘贴要统计的文本"
          @input="handleInput"
        />
      </div>

      <!-- 统计结果 -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ stats.characters }}</div>
          <div class="stat-label">字符数（含空格）</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-value">{{ stats.charactersNoSpace }}</div>
          <div class="stat-label">字符数（不含空格）</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-value">{{ stats.words }}</div>
          <div class="stat-label">单词数</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-value">{{ stats.lines }}</div>
          <div class="stat-label">行数</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-value">{{ stats.paragraphs }}</div>
          <div class="stat-label">段落数</div>
        </div>
        
        <div class="stat-card">
          <div class="stat-value">{{ stats.chinese }}</div>
          <div class="stat-label">中文字数</div>
        </div>
      </div>

      <!-- 详细统计 -->
      <div class="detailed-stats">
        <el-collapse>
          <el-collapse-item title="详细统计" name="1">
            <div class="detailed-stats-content">
              <div class="stat-row">
                <span>数字：</span>
                <span>{{ stats.numbers }}</span>
              </div>
              <div class="stat-row">
                <span>字母：</span>
                <span>{{ stats.letters }}</span>
              </div>
              <div class="stat-row">
                <span>标点符号：</span>
                <span>{{ stats.punctuation }}</span>
              </div>
              <div class="stat-row">
                <span>空格：</span>
                <span>{{ stats.spaces }}</span>
              </div>
              <div class="stat-row">
                <span>平均词长：</span>
                <span>{{ stats.avgWordLength.toFixed(1) }} 字符</span>
              </div>
              <div class="stat-row">
                <span>阅读时间：</span>
                <span>约 {{ stats.readingTime }} 分钟</span>
              </div>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>说明</h3>
        <ul>
          <li>支持中英文混合统计</li>
          <li>单词数统计基于空格和标点符号分隔</li>
          <li>段落数统计基于空行分隔</li>
          <li>阅读时间基于每分钟阅读 300 字计算</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { ElMessage } from 'element-plus'

const text = ref('')
const stats = reactive({
  characters: 0,
  charactersNoSpace: 0,
  words: 0,
  lines: 0,
  paragraphs: 0,
  chinese: 0,
  numbers: 0,
  letters: 0,
  punctuation: 0,
  spaces: 0,
  avgWordLength: 0,
  readingTime: 0
})

// 从剪贴板粘贴
const pasteFromClipboard = async () => {
  try {
    const clipboardText = await navigator.clipboard.readText()
    text.value = clipboardText
    handleInput()
    ElMessage.success('粘贴成功')
  } catch (error) {
    ElMessage.error('无法访问剪贴板')
  }
}

// 清空文本
const clearText = () => {
  text.value = ''
  handleInput()
}

// 统计文本
const handleInput = () => {
  const content = text.value

  // 基础统计
  stats.characters = content.length
  stats.charactersNoSpace = content.replace(/\s/g, '').length
  stats.words = content.trim().split(/\s+/).filter(word => word.length > 0).length
  stats.lines = content ? content.split('\n').length : 0
  stats.paragraphs = content ? content.split(/\n\s*\n/).filter(para => para.trim().length > 0).length : 0
  
  // 中文字数
  stats.chinese = (content.match(/[\u4e00-\u9fa5]/g) || []).length
  
  // 详细统计
  stats.numbers = (content.match(/\d/g) || []).length
  stats.letters = (content.match(/[a-zA-Z]/g) || []).length
  stats.punctuation = (content.match(/[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~。，、；：？！…—·ˉ¨''""々～‖∶＂＇｀｜〃〔〕〈〉《》「」『』．〖〗【】（）［］｛｝]/g) || []).length
  stats.spaces = (content.match(/\s/g) || []).length
  
  // 平均词长
  const words = content.trim().split(/\s+/).filter(word => word.length > 0)
  const totalLength = words.reduce((sum, word) => sum + word.length, 0)
  stats.avgWordLength = words.length ? totalLength / words.length : 0
  
  // 阅读时间（假设每分钟阅读300字）
  const totalChars = stats.chinese + stats.words
  stats.readingTime = Math.ceil(totalChars / 300)
}
</script>

<style scoped>
.word-count {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.count-container {
  margin-top: 20px;
}

.section {
  margin: 20px 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.actions {
  display: flex;
  gap: 10px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.stat-card {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 8px;
}

.stat-label {
  color: #606266;
  font-size: 14px;
}

.detailed-stats {
  margin: 20px 0;
}

.detailed-stats-content {
  padding: 10px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #EBEEF5;
}

.stat-row:last-child {
  border-bottom: none;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

:deep(.el-textarea__inner) {
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}
</style> 